import { useMemo } from "react";

import { useGenerationSettings } from "~/entities/model";
import {
    findModeConfig,
    type ArtifactType,
    type GenerationMode,
    type ModeConfig,
    type ModelConfig,
} from "~/shared/data/chat-models";

/**
 * Наборы, производные от каталога моделей и текущего выбора: какие модели/режимы
 * доступны и какие оси показывает панель. Ничего не пишет в стор — только читает
 * каталог, поэтому решение «что показать» отделено от решения «что подправить».
 */
export function useChatSettingsOptions(
    artifactType: ArtifactType,
    modelId: string | null,
    mode: GenerationMode,
) {
    const settingsQuery = useGenerationSettings();
    const allModels: readonly ModelConfig[] = useMemo(() => settingsQuery.data ?? [], [settingsQuery.data]);
    const availableModels = useMemo(
        () => allModels.filter((m) => m.artifactType === artifactType),
        [allModels, artifactType],
    );
    const currentModel = useMemo(() => allModels.find((m) => m.id === modelId) ?? null, [allModels, modelId]);

    const availableModes: readonly GenerationMode[] = useMemo(
        () => currentModel?.modes.map((m) => m.name) ?? [],
        [currentModel],
    );
    const currentMode: ModeConfig | null = useMemo(
        () => findModeConfig(currentModel, mode),
        [currentModel, mode],
    );

    const availableAspectRatios = useMemo(() => currentMode?.aspectRatios ?? [], [currentMode]);
    const availableResolutions = useMemo(() => currentMode?.resolutions ?? [], [currentMode]);
    const availableDurations = useMemo(() => currentMode?.durations ?? [], [currentMode]);
    const availableCharacterOrientations = useMemo(
        () => currentMode?.characterOrientations ?? [],
        [currentMode],
    );
    const availableUpscaleFactors = useMemo(() => currentMode?.upscaleFactors ?? [], [currentMode]);

    return {
        isLoadingModels: settingsQuery.isLoading,
        availableModels,
        currentModel,
        availableModes,
        currentMode,
        availableAspectRatios,
        availableResolutions,
        availableDurations,
        availableCharacterOrientations,
        availableUpscaleFactors,
        showAudio: !!currentMode && currentMode.audioSupport !== "none",
        audioLocked: currentMode?.audioSupport === "always",
        // Потолок пакета режима. Настройки ещё не загрузились (режим не резолвится)
        // — считаем, что пакета нет: обещать плитку, которую некому исполнить,
        // хуже, чем показать её заблокированной лишнюю секунду.
        maxBatchSize: currentMode?.maxBatchSize ?? 1,
        // Ось показываем только когда есть чей потолок показывать. Нет ни одной
        // модели (все провайдеры выключены) — прочие оси уходят вместе со своими
        // наборами, и пакет, оставшийся на панели один, говорил бы о настройках
        // несуществующей генерации.
        showBatchSize: !!currentMode,
    };
}

export type ChatSettingsOptions = ReturnType<typeof useChatSettingsOptions>;
